/*
 * PlaneState — the honest plane-status panel.
 *
 * The four non-Transport plane services are now DEPLOYED as health-only stubs
 * (reachable same-origin at /v1/planes/<plane>/healthz → 200); their business
 * read-APIs are still a future increment. So an "offline" surface is no longer
 * the same as "service not running" — it depends on a LIVE health probe:
 *
 *   serviceHealthy === true  → the container is up and /healthz returns 200.
 *     We render the CALM-BUT-POSITIVE "deployed · health 200 · API pending"
 *     treatment (indigo family, mirroring Overview's DeployedStratum). The
 *     panel says the SERVICE is deployed and healthy, and that THIS specific
 *     read-API is a pending increment — never "not running". Listed endpoints
 *     become "Business API will bind to".
 *
 *   serviceHealthy !== true  → the probe is down (or no probe was supplied).
 *     We keep the existing honest "offline — awaiting service" wording.
 *
 * The distinction is real, driven by the probe — never assume-healthy. Loud red
 * is reserved for genuine `kind:"error"` failures (a live call that broke), not
 * for "deployed/pending" and not for "awaiting". State is conveyed by text +
 * icon, not colour alone (WCAG 1.4.1).
 *
 * Also used for generic loud error / loading states inside live views.
 */

import type { PlaneOfflineError } from "../api/client";
import { SpecRef } from "./SpecRef";

interface PlaneStateProps {
  /**
   * "offline" for a missing service, "error" for a live-call failure,
   * "loading", "empty" for a live-but-nothing-yet result, and "gated" for a
   * LIVE service that correctly refused an unauthenticated read (403) — a
   * working access control, not a missing service and not a failure.
   */
  kind: "offline" | "error" | "loading" | "empty" | "gated";
  /** The PlaneOfflineError carries plane + service + hint. */
  offline?: PlaneOfflineError;
  /** Title override. */
  title?: string;
  /** Body message. */
  message?: string;
  /** Endpoints this plane will bind to once the service is up. */
  endpoints?: string[];
  /** Spec ref, e.g. "Part III §15–§23". */
  specRef?: string;
  /** Retry handler for the error kind. */
  onRetry?: () => void;
  /**
   * Live health-probe verdict for the plane backing an `offline` surface.
   * `true` → the service is DEPLOYED and reporting healthy (/healthz 200); the
   * panel renders the calm "deployed · health 200 · API pending" state instead
   * of "service not running". `false`/undefined → the honest offline state.
   * Ignored for non-offline kinds. Must reflect a REAL successful probe.
   */
  serviceHealthy?: boolean;
  /**
   * The health probe is still IN FLIGHT (not yet settled up/down). On the
   * slow/intermittent national networks this console targets, the probe can
   * take seconds — so while probing we must NOT flash a false "service not
   * running". When `kind:"offline"` and this is `true`, the panel renders a
   * NEUTRAL "checking…" state instead of the offline wording. Precedence:
   * deployed (serviceHealthy) > probing > settled-offline.
   */
  serviceProbing?: boolean;
  /** Health-probe round-trip latency in ms, shown as evidence when healthy. */
  serviceLatencyMs?: number;
}

export function PlaneState({
  kind,
  offline,
  title,
  message,
  endpoints,
  specRef,
  onRetry,
  serviceHealthy,
  serviceProbing,
  serviceLatencyMs,
}: PlaneStateProps) {
  // The deployed-but-pending state: an offline surface whose backing service's
  // health probe is UP. Calm, positive, indigo — never the loud red of failure.
  const deployed = kind === "offline" && serviceHealthy === true;

  if (deployed) {
    return (
      <DeployedPanel
        offline={offline}
        title={title}
        message={message}
        endpoints={endpoints}
        specRef={specRef}
        latencyMs={serviceLatencyMs}
      />
    );
  }

  if (kind === "gated") {
    return (
      <GatedPanel title={title} message={message} endpoints={endpoints} specRef={specRef} />
    );
  }

  // Probe still in flight (and not yet known-up): show a NEUTRAL "checking…"
  // state rather than flashing a false "service not running" on slow networks.
  // Precedence is deployed > probing > settled-offline.
  if (kind === "offline" && serviceProbing === true) {
    return <ProbingPanel offline={offline} specRef={specRef} />;
  }

  const loud = kind === "offline" || kind === "error";
  const accent = loud ? "var(--red)" : kind === "loading" ? "var(--ember)" : "var(--fg-faint)";

  const heading =
    title ??
    (kind === "offline"
      ? `${offline?.plane ?? "Plane"} offline — awaiting service`
      : kind === "error"
        ? "Live call failed"
        : kind === "loading"
          ? "Reading live data…"
          : "Nothing to show yet");

  const body =
    message ??
    (kind === "offline"
      ? offline?.hint ??
        "This plane's backing service is not running in this deployment."
      : kind === "error"
        ? "The sidecar did not return a usable response. This is shown loudly rather than hidden."
        : kind === "loading"
          ? "Polling the sidecar."
          : "No records have been observed yet.");

  return (
    <div
      role={loud ? "alert" : "status"}
      className="panel"
      style={{
        borderColor: loud ? "rgba(var(--red-rgb),0.35)" : "var(--hair)",
        background: loud ? "var(--red-dim)" : "var(--bg-2)",
        display: "flex",
        flexDirection: "column",
        gap: "12px",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <span
          aria-hidden="true"
          style={{
            width: "10px",
            height: "10px",
            borderRadius: "50%",
            background: accent,
            boxShadow: loud ? `0 0 0 4px var(--red-dim)` : "none",
            flex: "0 0 auto",
          }}
        />
        <h2 style={{ fontSize: "20px", color: loud ? "var(--red)" : "var(--fg)" }}>
          {heading}
        </h2>
      </div>

      <p style={{ color: "var(--fg-muted)", margin: 0, maxWidth: "60ch" }}>{body}</p>

      {offline?.service && (
        <div
          className="mono"
          style={{ fontSize: "12.5px", color: "var(--fg-faint)" }}
        >
          awaiting service:{" "}
          <span style={{ color: "var(--fg)" }}>{offline.service}</span>
        </div>
      )}

      {endpoints && endpoints.length > 0 && (
        <div>
          <div
            className="mono"
            style={{
              fontSize: "11px",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "var(--fg-faint)",
              marginBottom: "6px",
            }}
          >
            Will bind to
          </div>
          <ul
            style={{
              listStyle: "none",
              margin: 0,
              padding: 0,
              display: "flex",
              flexDirection: "column",
              gap: "4px",
            }}
          >
            {endpoints.map((e) => (
              <li
                key={e}
                className="mono-surface"
                style={{ color: "var(--fg-muted)" }}
              >
                {e}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "16px",
          flexWrap: "wrap",
        }}
      >
        {specRef && (
          <span className="mono" style={{ fontSize: "12px", color: "var(--indigo)" }}>
            <SpecRef text={specRef} />
          </span>
        )}
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            style={{
              minHeight: "44px",
              padding: "0 16px",
              borderRadius: "8px",
              border: "1px solid var(--hair-strong)",
              background: "var(--bg)",
              color: "var(--fg)",
              fontFamily: "var(--font-mono)",
              fontSize: "13px",
            }}
          >
            Retry
          </button>
        )}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------- *
 * DEPLOYED — calm-but-positive indigo panel. The backing service's health  *
 * stub is UP (/healthz 200); only THIS read-API is a pending increment.    *
 * Mirrors Overview's DeployedStratum: word-bearing "deployed" + "health    *
 * 200" + "API pending" chips, indigo tokens, role="status" (polite). The   *
 * signal is carried by TEXT + icon, never colour alone (WCAG 1.4.1).       *
 * ---------------------------------------------------------------------- */

function DeployedPanel({
  offline,
  title,
  message,
  endpoints,
  specRef,
  latencyMs,
}: {
  offline?: PlaneOfflineError;
  title?: string;
  message?: string;
  endpoints?: string[];
  specRef?: string;
  latencyMs?: number;
}) {
  const plane = offline?.plane ?? "Plane";

  const heading = title ?? `${plane} service deployed — read-API pending`;

  const body =
    message ??
    "The backing service container is up and reporting healthy. This read-API is a future increment, so no business data is shown yet — the console will not fabricate it.";

  return (
    <div
      role="status"
      className="panel"
      style={{
        borderColor: "rgba(var(--indigo-rgb),0.4)",
        background: "var(--indigo-dim)",
        display: "flex",
        flexDirection: "column",
        gap: "12px",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <span
          aria-hidden="true"
          style={{
            width: "10px",
            height: "10px",
            borderRadius: "50%",
            background: "rgb(var(--indigo-rgb))",
            boxShadow: "0 0 0 4px var(--indigo-dim)",
            flex: "0 0 auto",
          }}
        />
        <h2 style={{ fontSize: "20px", color: "var(--fg)" }}>{heading}</h2>
      </div>

      {/* Word-bearing status chips — the signal is text, not colour. */}
      <div
        className="mono"
        style={{
          fontSize: "12px",
          display: "flex",
          alignItems: "center",
          gap: "8px",
          flexWrap: "wrap",
        }}
      >
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "5px",
            padding: "2px 8px",
            borderRadius: "999px",
            color: "var(--indigo)",
            border: "1px solid rgba(var(--indigo-rgb), 0.4)",
            background: "var(--indigo-dim)",
          }}
        >
          <span aria-hidden="true">▣</span> deployed
        </span>
        <span style={{ color: "var(--fg-muted)" }}>
          health 200{latencyMs !== undefined ? ` · ${latencyMs} ms` : ""}
        </span>
        <span style={{ color: "var(--fg-faint)" }}>· API pending</span>
      </div>

      <p style={{ color: "var(--fg-muted)", margin: 0, maxWidth: "60ch" }}>{body}</p>

      {offline?.service && (
        <div className="mono" style={{ fontSize: "12.5px", color: "var(--fg-faint)" }}>
          service:{" "}
          <span style={{ color: "var(--fg)" }}>{offline.service}</span>{" "}
          <span style={{ color: "var(--indigo)" }}>· deployed &amp; healthy</span>
        </div>
      )}

      {endpoints && endpoints.length > 0 && (
        <div>
          <div
            className="mono"
            style={{
              fontSize: "11px",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "var(--fg-faint)",
              marginBottom: "6px",
            }}
          >
            Business API will bind to
          </div>
          <ul
            style={{
              listStyle: "none",
              margin: 0,
              padding: 0,
              display: "flex",
              flexDirection: "column",
              gap: "4px",
            }}
          >
            {endpoints.map((e) => (
              <li key={e} className="mono-surface" style={{ color: "var(--fg-muted)" }}>
                {e}
              </li>
            ))}
          </ul>
        </div>
      )}

      {specRef && (
        <span className="mono" style={{ fontSize: "12px", color: "var(--indigo)" }}>
          <SpecRef text={specRef} />
        </span>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------------- *
 * PROBING — neutral "checking…" panel. The health probe is in flight and  *
 * its verdict is not yet known. On slow/intermittent networks this window  *
 * can last seconds, so we render a CALM checking state rather than flash a  *
 * false "service not running". No red, no "offline", no "awaiting" — the    *
 * truth is simply "we do not know yet". role="status" (polite).            *
 * ---------------------------------------------------------------------- */

function ProbingPanel({
  offline,
  specRef,
}: {
  offline?: PlaneOfflineError;
  specRef?: string;
}) {
  const plane = offline?.plane ?? "Plane";

  return (
    <div
      role="status"
      aria-busy="true"
      className="panel"
      style={{
        borderColor: "var(--hair)",
        background: "var(--bg-2)",
        display: "flex",
        flexDirection: "column",
        gap: "12px",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <span
          aria-hidden="true"
          style={{
            width: "10px",
            height: "10px",
            borderRadius: "50%",
            background: "rgba(var(--indigo-rgb),0.55)",
            flex: "0 0 auto",
          }}
        />
        <h2 style={{ fontSize: "20px", color: "var(--fg)" }}>
          Checking {plane} service health…
        </h2>
      </div>

      <p className="mono" style={{ color: "var(--fg-faint)", margin: 0, fontSize: "12.5px" }}>
        <span aria-hidden="true">◷</span> Probing the deployed health stub. No
        status is asserted until the probe settles.
      </p>

      {specRef && (
        <span className="mono" style={{ fontSize: "12px", color: "var(--indigo)" }}>
          <SpecRef text={specRef} />
        </span>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------------- *
 * GATED — a LIVE service that correctly refused an unauthenticated read   *
 * (HTTP 403). This is neither "offline" (the service IS up and answering) *
 * nor a scary failure (the gate is doing exactly its job, §39.3.1/§47.2). *
 * Gold/neutral tokens — deliberately distinct from both the red of a real *
 * failure and the indigo of "deployed, API pending". Text carries the     *
 * signal ("live", "access-gated"), never colour alone (WCAG 1.4.1).       *
 * ---------------------------------------------------------------------- */

function GatedPanel({
  title,
  message,
  endpoints,
  specRef,
}: {
  title?: string;
  message?: string;
  endpoints?: string[];
  specRef?: string;
}) {
  const heading = title ?? "Live · access-gated (operator credential required)";
  const body =
    message ??
    "This surface answered live, and correctly refused an unauthenticated read. It requires an authenticated operator/DPA credential this console session does not carry — that is the gate working, not a missing service and not a failure.";

  return (
    <div
      role="status"
      className="panel"
      style={{
        borderColor: "rgba(var(--gold-rgb),0.4)",
        background: "var(--gold-dim)",
        display: "flex",
        flexDirection: "column",
        gap: "12px",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <span
          aria-hidden="true"
          style={{
            width: "10px",
            height: "10px",
            borderRadius: "50%",
            background: "rgb(var(--gold-rgb))",
            boxShadow: "0 0 0 4px var(--gold-dim)",
            flex: "0 0 auto",
          }}
        />
        <h2 style={{ fontSize: "20px", color: "var(--fg)" }}>{heading}</h2>
      </div>

      <div
        className="mono"
        style={{ fontSize: "12px", display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}
      >
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "5px",
            padding: "2px 8px",
            borderRadius: "999px",
            color: "var(--gold)",
            border: "1px solid rgba(var(--gold-rgb), 0.4)",
            background: "var(--gold-dim)",
          }}
        >
          <span aria-hidden="true">🔒</span> live
        </span>
        <span style={{ color: "var(--fg-muted)" }}>HTTP 403</span>
        <span style={{ color: "var(--fg-faint)" }}>· operator credential required</span>
      </div>

      <p style={{ color: "var(--fg-muted)", margin: 0, maxWidth: "60ch" }}>{body}</p>

      {endpoints && endpoints.length > 0 && (
        <div>
          <div
            className="mono"
            style={{
              fontSize: "11px",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "var(--fg-faint)",
              marginBottom: "6px",
            }}
          >
            Gated route
          </div>
          <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: "4px" }}>
            {endpoints.map((e) => (
              <li key={e} className="mono-surface" style={{ color: "var(--fg-muted)" }}>
                {e}
              </li>
            ))}
          </ul>
        </div>
      )}

      {specRef && (
        <span className="mono" style={{ fontSize: "12px", color: "var(--indigo)" }}>
          <SpecRef text={specRef} />
        </span>
      )}
    </div>
  );
}
